// BHP-1124: the confirm-in-place step shared by the VPS power actions and
// create, DNS record removal and domain cancellation. The caller keeps the
// surrounding block and the wording, because it knows what is about to happen;
// this component owns the question's place, the button pair and what assistive
// tech hears. Cancelling only ever calls onCancel, and both buttons are
// disabled while the confirmed action runs.
//
// - The question is always the `prompt`, so the pair can be a labelled group:
//   a screen reader hears what "Confirm" confirms, not just "Confirm, button".
// - Focus moves to the cancel button when the step appears. The control that
//   opened it is gone by then, so focus would otherwise drop to <body> (and
//   inside a dialog, out of its focus trap); cancel is the safe default.
import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';

interface InlineConfirmProps {
  prompt: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** 'stacked': question above the buttons; 'inline': on one row (table cells). */
  layout?: 'stacked' | 'inline';
  tone?: 'danger' | 'warning';
  size?: 'sm' | 'md';
  isPending?: boolean;
  pendingLabel?: string;
  className?: string;
}

const TONE: Record<NonNullable<InlineConfirmProps['tone']>, string> = {
  danger: 'bg-rose-600 hover:bg-rose-700',
  warning: 'bg-amber-600 hover:bg-amber-700'
};

const SIZE: Record<NonNullable<InlineConfirmProps['size']>, string> = {
  sm: 'px-2 py-1 text-xs',
  md: 'px-3 py-1.5 text-sm'
};

export default function InlineConfirm({
  prompt,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  layout = 'stacked',
  tone = 'danger',
  size = 'md',
  isPending = false,
  pendingLabel,
  className = ''
}: InlineConfirmProps): JSX.Element {
  const promptId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const buttons = (
    <>
      <button
        type="button"
        disabled={isPending}
        onClick={onConfirm}
        className={`rounded-md border-0 ${SIZE[size]} font-medium text-white ${TONE[tone]} disabled:opacity-50`}
      >
        {isPending ? (pendingLabel ?? confirmLabel) : confirmLabel}
      </button>
      <button
        ref={cancelRef}
        type="button"
        disabled={isPending}
        onClick={onCancel}
        className={`rounded-md border border-solid border-slate-300 bg-white ${SIZE[size]} font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50`}
      >
        {cancelLabel}
      </button>
    </>
  );

  if (layout === 'inline') {
    return (
      <div
        role="group"
        aria-labelledby={promptId}
        className={`flex items-center gap-2 ${className}`}
      >
        <span id={promptId}>{prompt}</span>
        {buttons}
      </div>
    );
  }

  return (
    <div role="group" aria-labelledby={promptId} className={className}>
      <div id={promptId}>{prompt}</div>
      <div className="mt-2 flex items-center gap-2">{buttons}</div>
    </div>
  );
}
